import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, resolveComponent as _resolveComponent, normalizeClass as _normalizeClass, resolveDirective as _resolveDirective, createVNode as _createVNode, withDirectives as _withDirectives, withCtx as _withCtx, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createBlock as _createBlock, createElementVNode as _createElementVNode, vShow as _vShow, renderList as _renderList, Fragment as _Fragment } from "vue"

const _hoisted_1 = { class: "create-translation-project-container" }
const _hoisted_2 = { class: "catalog-selector-container" }
const _hoisted_3 = { class: "spinner-container" }
const _hoisted_4 = { class: "translation-group-selection-container" }
const _hoisted_5 = { class: "translation-group-selection-count" }
const _hoisted_6 = { class: "language-selection-container" }
const _hoisted_7 = { key: 0 }
const _hoisted_8 = { key: 0 }

import { getLanguageDisplayName } from "@/util/languages";
import ManageCatalogs from "@ChannelCreator/components/ManageCatalogs.vue";
import Dropdown from "@common/components/Dropdown.vue";
import Icon from "@common/components/Icon.vue";
import Spinner from "@common/components/Spinner.vue";
import { useContentLanguages } from "@common/composable/useMetaDataStore";
import { ValidationFeedbackType } from "@medapro-types/common/common-enum";
import { CreateTranslationProjectData, TranslationGroupType } from "@medapro-types/TranslationManager/translationManager";
import translationManagerApi from "@services/translationManager";
import { useTranslationManagerState } from "@TranslationManager/composables/useTranslationManagerStore";
import { generateNewName } from "@util/generateNewName";
import { naturalSort } from "@util/naturalSort";
import ruleEvaluator from "@util/validations/ruleEvaluator";
import _ from "lodash";
import { computed, reactive, watch } from "vue";
import { useI18n } from "vue-i18n";

//#region General
const warningThreshold = 100000;
//#endregion

//#region Store
type ViewModel = { data: CreateTranslationProjectData; isLoading: boolean };


export default /*#__PURE__*/_defineComponent({
  __name: 'CreateTranslationProjectModal',
  props: {
    show: { type: Boolean },
    translationProjectsNames: {}
  },
  emits: ["update:show", "confirm", "cancel"],
  setup(__props: any, { emit: emits }) {

const props = __props;




const { t: $t } = useI18n();

const { catalogs } = useTranslationManagerState();
//#endregion

//#region Languages
const languages = computed(() => {
  return contentLanguages.value.available.map((lang) => {
    return { languageId: lang.languageId, displayName: getLanguageDisplayName(lang.languageCode) };
  });
});
//#endregion

//#region Store
const { contentLanguages } = useContentLanguages();
//#endregion

//#region ViewModel
const vm = reactive<ViewModel>({
  data: {
    projectName: "",
    catalogId: null,
    sourceLanguageId: null,
    targetLanguageId: null,
    groups: [],
    showOnlyOutdated: false
  },
  isLoading: false
});
//#endregion

//#region Translation group selection
const updateGroupCounts = async () => {
  if (!vm.data.catalogId || !vm.data.sourceLanguageId || !vm.data.targetLanguageId) {
    return;
  }

  vm.isLoading = true;

  try {
    const sourceLanguageCode = contentLanguages.value.available.find((lang) => lang.languageId === vm.data.sourceLanguageId)?.languageCode;
    const targetLanguageCode = contentLanguages.value.available.find((lang) => lang.languageId === vm.data.targetLanguageId)?.languageCode;
    if (!sourceLanguageCode || !targetLanguageCode) {
      return;
    }

    const getEnabled = (groupType: TranslationGroupType) => {
      const previousGroup = vm.data.groups.find((prev) => prev.type === groupType);
      if (previousGroup) {
        return previousGroup.enabled;
      }

      return true;
    };
    const groupCounts = await translationManagerApi.getValueCounts(vm.data.catalogId, sourceLanguageCode, targetLanguageCode, vm.data.showOnlyOutdated);
    const newGroups = [
      {
        type: TranslationGroupType.ArticleDescriptions,
        entryCount: groupCounts.articleDescriptionCount ?? 0,
        enabled: getEnabled(TranslationGroupType.ArticleDescriptions)
      },
      {
        type: TranslationGroupType.ArticleNames,
        entryCount: groupCounts.articleNameCount ?? 0,
        enabled: getEnabled(TranslationGroupType.ArticleNames)
      },
      {
        type: TranslationGroupType.AttributeUnits,
        entryCount: groupCounts.attributeUnitCount ?? 0,
        enabled: getEnabled(TranslationGroupType.AttributeUnits)
      },
      {
        type: TranslationGroupType.AttributeValues,
        entryCount: groupCounts.attributeValueCount ?? 0,
        enabled: getEnabled(TranslationGroupType.AttributeValues)
      },
      {
        type: TranslationGroupType.CatalogGroupAttributes,
        entryCount: groupCounts.catalogNodeAttributeCount ?? 0,
        enabled: getEnabled(TranslationGroupType.CatalogGroupAttributes)
      },
      {
        type: TranslationGroupType.CatalogGroupDescriptions,
        entryCount: groupCounts.catalogNodeDescriptionCount ?? 0,
        enabled: getEnabled(TranslationGroupType.CatalogGroupDescriptions)
      },
      {
        type: TranslationGroupType.CatalogGroupNames,
        entryCount: groupCounts.catalogNodeCount ?? 0,
        enabled: getEnabled(TranslationGroupType.CatalogGroupNames)
      },
      {
        type: TranslationGroupType.GlobalAttributeUnits,
        entryCount: groupCounts.globalAttributeUnitCount ?? 0,
        enabled: getEnabled(TranslationGroupType.GlobalAttributeUnits)
      },
      {
        type: TranslationGroupType.GlobalAttributeValues,
        entryCount: groupCounts.globalArticleAttributeCount ?? 0,
        enabled: getEnabled(TranslationGroupType.GlobalAttributeValues)
      },
      {
        type: TranslationGroupType.Keywords,
        entryCount: groupCounts.keywordCount ?? 0,
        enabled: getEnabled(TranslationGroupType.Keywords)
      },
      {
        type: TranslationGroupType.VariationGroups,
        entryCount: groupCounts.variationGroupCount ?? 0,
        enabled: getEnabled(TranslationGroupType.VariationGroups)
      }
    ];

    vm.data.groups = newGroups;
  } catch (error) {
    console.error(error);
  } finally {
    vm.isLoading = false;
  }
};

const translationGroupEntryCount = computed(() => {
  return _.chain(vm.data.groups)
    .filter((group) => group.enabled)
    .sumBy((group) => group.entryCount)
    .value();
});
//#endregion

//#region Catalog selection
const sortedCatalogs = computed(() => {
  return naturalSort(catalogs.value, "name");
});

const selectCatalog = async (catalog: string | undefined) => {
  vm.data.catalogId = catalog ?? null;
  await updateGroupCounts();
};
//#endregion

//#region Language selection
const sourceLanguages = computed(() => {
  return languages.value;
});

const targetLanguages = computed(() => {
  return languages.value.filter((language) => !vm.data.sourceLanguageId || vm.data.sourceLanguageId !== language.languageId);
});

const selectSourceLanguage = async (languageId: number | undefined) => {
  if (vm.data.targetLanguageId === languageId) {
    vm.data.targetLanguageId = vm.data.sourceLanguageId;
  }

  vm.data.sourceLanguageId = languageId ?? null;
  await updateGroupCounts();
};

const selectTargetLanguage = async (languageId: number | undefined) => {
  vm.data.targetLanguageId = languageId ?? null;
  await updateGroupCounts();
};
//#endregion

//#region Show only outdated handling
watch(
  () => vm.data.showOnlyOutdated,
  async (newValue, oldValue) => {
    if (newValue !== oldValue) {
      await updateGroupCounts();
    }
  }
);
//#endregion

//#region Validation
const getVariant = (type: ValidationFeedbackType) => {
  switch (type) {
    case ValidationFeedbackType.warning:
      return "warning";
    case ValidationFeedbackType.success:
      return "info";
    default:
      return "danger";
  }
};

const calculateTranslationGroupEntryCount = (data: CreateTranslationProjectData) => {
  return _.chain(data.groups)
    .filter((group) => group.enabled)
    .sumBy((group) => group.entryCount)
    .value();
};

const validationFeedbacks = computed(() => {
  const rules = {
    projectName: [
      {
        rule: (entry: CreateTranslationProjectData) => !entry.projectName,
        msg: $t("validation.valueRequired", { field: $t("name") }),
        severity: ValidationFeedbackType.error
      },
      {
        rule: (entry: CreateTranslationProjectData) =>
          entry.projectName !== null &&
          props.translationProjectsNames.some((x) => {
            return x.toLowerCase() == vm.data.projectName?.toLowerCase();
          }),
        msg: $t("validation.duplicateExists", {
          field: $t("name")
        }),
        severity: ValidationFeedbackType.error
      },
      {
        rule: (entry: CreateTranslationProjectData) => (entry.projectName ? entry.projectName.length > 128 : false),
        msg: $t("validation.lengthExceeds", {
          field: $t("name", 1),
          length: 128
        }),
        severity: ValidationFeedbackType.error
      }
    ],
    groups: [
      {
        rule: (entry: CreateTranslationProjectData) => !_.some(entry.groups, (group) => group.entryCount > 0),
        msg: $t("translationManager.createNewDialog.noGroupValuesWarning"),
        severity: ValidationFeedbackType.error
      },
      {
        rule: (entry: CreateTranslationProjectData) => calculateTranslationGroupEntryCount(entry) === 0,
        msg: $t("translationManager.createNewDialog.noSelectedGroupWarning"),
        severity: ValidationFeedbackType.error
      },
      {
        rule: (entry: CreateTranslationProjectData) => calculateTranslationGroupEntryCount(entry) > warningThreshold,
        msg: $t("translationManager.createNewDialog.valueCountWarning", { count: warningThreshold }),
        severity: ValidationFeedbackType.warning
      }
    ]
  };

  const feedbacks = ruleEvaluator(vm.data, rules);
  return feedbacks;
});

const hasValidationErrorInternal = computed(() => {
  return Object.values(validationFeedbacks.value).some((feedback) => feedback.type === ValidationFeedbackType.error);
});
//#endregion

//#region Modal handling
const canCreate = computed(() => {
  return contentLanguages.value.available.length > 1 && vm.data.sourceLanguageId && vm.data.targetLanguageId && translationGroupEntryCount.value > 0;
});

const localShow = computed<boolean>({
  get: () => props.show,
  set: (nv) => emits("update:show", nv)
});

const onCancel = () => {
  emits("cancel", true);
};

const onShow = async () => {
  const projectNames = props.translationProjectsNames.map((name) => {
    return { name };
  });
  vm.data.projectName = generateNewName($t("translationManager.createNewDialog.defaultProjectName"), projectNames);

  if (sortedCatalogs.value.length > 0) {
    vm.data.catalogId = sortedCatalogs.value[0].catalogId;
  }

  if (sourceLanguages.value.length > 0) {
    vm.data.sourceLanguageId = sourceLanguages.value[0].languageId;
  }

  if (targetLanguages.value.length > 0) {
    vm.data.targetLanguageId = targetLanguages.value[0].languageId;
  }

  await updateGroupCounts();
};

const onConfirm = () => {
  localShow.value = false;
  emits("confirm", vm.data);
};
//#endregion

return (_ctx: any,_cache: any) => {
  const _component_b_input = _resolveComponent("b-input")!
  const _component_b_form_group = _resolveComponent("b-form-group")!
  const _component_b_btn = _resolveComponent("b-btn")!
  const _component_b_form_checkbox = _resolveComponent("b-form-checkbox")!
  const _component_b_badge = _resolveComponent("b-badge")!
  const _component_b_modal = _resolveComponent("b-modal")!
  const _directive_tooltip = _resolveDirective("tooltip")!
  const _directive_b_modal = _resolveDirective("b-modal")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(_component_b_modal, {
      title: _unref($t)('translationManager.createNewDialog.title'),
      "body-class": "create-translation-project-modal",
      size: "lg",
      modelValue: localShow.value,
      "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((localShow).value = $event)),
      "header-close-content": "",
      onShow: onShow,
      onOk: onConfirm,
      onCancel: onCancel
    }, {
      "modal-footer": _withCtx(({ ok, cancel }) => [
        _createVNode(_component_b_btn, {
          onClick: ok,
          variant: "primary",
          disabled: hasValidationErrorInternal.value || !canCreate.value
        }, {
          default: _withCtx(() => [
            _createTextVNode(_toDisplayString(_unref($t)("OK")), 1)
          ]),
          _: 2
        }, 1032, ["onClick", "disabled"]),
        _createVNode(_component_b_btn, { onClick: cancel }, {
          default: _withCtx(() => [
            _createTextVNode(_toDisplayString(_unref($t)("Cancel")), 1)
          ]),
          _: 2
        }, 1032, ["onClick"])
      ]),
      default: _withCtx(() => [
        _createElementVNode("div", _hoisted_1, [
          _createVNode(_component_b_form_group, {
            label: _unref($t)('name')
          }, {
            default: _withCtx(() => [
              _withDirectives(_createVNode(_component_b_input, {
                modelValue: vm.data.projectName,
                "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((vm.data.projectName) = $event)),
                class: _normalizeClass({ 'border-danger': !validationFeedbacks.value.projectName.isValid })
              }, null, 8, ["modelValue", "class"]), [
                [_directive_tooltip, validationFeedbacks.value.projectName.feedback]
              ])
            ]),
            _: 1
          }, 8, ["label"]),
          _createVNode(_component_b_form_group, null, {
            label: _withCtx(() => [
              _createTextVNode(_toDisplayString(_unref($t)("Catalogue")), 1)
            ]),
            default: _withCtx(() => [
              _createElementVNode("div", _hoisted_2, [
                _createVNode(Dropdown, {
                  modelValue: vm.data.catalogId,
                  "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((vm.data.catalogId) = $event)),
                  items: sortedCatalogs.value || [],
                  "value-field": "catalogId",
                  "text-field": "name",
                  class: "catalog-selector-dropdown",
                  default: `<${_unref($t)('none')}>`,
                  onInput: selectCatalog
                }, {
                  "item-template": _withCtx(({ entry }) => [
                    entry
                      ? (_openBlock(), _createElementBlock("span", {
                          key: 0,
                          class: _normalizeClass({ 'font-italic': !entry.name && entry.fallbackName })
                        }, _toDisplayString(entry.name ? entry.name : entry.fallbackName), 3))
                      : _createCommentVNode("", true)
                  ]),
                  _: 1
                }, 8, ["modelValue", "items", "default"]),
                _withDirectives((_openBlock(), _createBlock(_component_b_btn, { class: "ml-1 btn-icon mono" }, {
                  default: _withCtx(() => [
                    _createVNode(Icon, { icon: "Open" })
                  ]),
                  _: 1
                })), [
                  [_directive_tooltip, _unref($t)('OpenCatalog')],
                  [
                    _directive_b_modal,
                    void 0,
                    void 0,
                    { "open-catalog": true }
                  ]
                ])
              ])
            ]),
            _: 1
          }),
          _withDirectives(_createElementVNode("div", _hoisted_3, [
            _createVNode(Spinner)
          ], 512), [
            [_vShow, vm.isLoading]
          ]),
          _withDirectives(_createElementVNode("div", _hoisted_4, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(vm.data.groups, (group) => {
              return (_openBlock(), _createBlock(_component_b_form_checkbox, {
                key: group.type,
                disabled: group.entryCount === 0,
                modelValue: group.enabled,
                "onUpdate:modelValue": ($event: any) => ((group.enabled) = $event)
              }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(_unref($t)(`translationManager.groups.${group.type}`)) + " (" + _toDisplayString(group.entryCount) + ")", 1)
                ]),
                _: 2
              }, 1032, ["disabled", "modelValue", "onUpdate:modelValue"]))
            }), 128))
          ], 512), [
            [_vShow, !vm.isLoading]
          ]),
          _withDirectives(_createElementVNode("div", _hoisted_5, [
            (!validationFeedbacks.value.groups.isValid)
              ? (_openBlock(), _createBlock(_component_b_badge, {
                  key: 0,
                  class: "translation-group-badge",
                  variant: getVariant(validationFeedbacks.value.groups.type),
                  pill: ""
                }, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(validationFeedbacks.value.groups.feedback), 1)
                  ]),
                  _: 1
                }, 8, ["variant"]))
              : (_openBlock(), _createBlock(_component_b_badge, {
                  key: 1,
                  class: "translation-group-badge",
                  variant: "light",
                  pill: ""
                }, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(_unref($t)("translationManager.createNewDialog.valueCount", { count: translationGroupEntryCount.value })), 1)
                  ]),
                  _: 1
                }))
          ], 512), [
            [_vShow, !vm.isLoading]
          ]),
          _createElementVNode("div", _hoisted_6, [
            _createVNode(_component_b_form_group, {
              label: _unref($t)('translationManager.sourceLanguage'),
              class: "language-selector"
            }, {
              default: _withCtx(() => [
                _createVNode(Dropdown, {
                  modelValue: vm.data.sourceLanguageId,
                  "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((vm.data.sourceLanguageId) = $event)),
                  items: sourceLanguages.value || [],
                  "value-field": "languageId",
                  "text-field": "displayName",
                  class: "language-selector-dropdown",
                  default: `<${_unref($t)('none')}>`,
                  onInput: selectSourceLanguage
                }, {
                  "item-template": _withCtx(({ entry }) => [
                    entry
                      ? (_openBlock(), _createElementBlock("span", _hoisted_7, _toDisplayString(entry.displayName), 1))
                      : _createCommentVNode("", true)
                  ]),
                  _: 1
                }, 8, ["modelValue", "items", "default"])
              ]),
              _: 1
            }, 8, ["label"]),
            _createVNode(_component_b_form_group, {
              label: _unref($t)('translationManager.targetLanguage'),
              class: "language-selector"
            }, {
              default: _withCtx(() => [
                _createVNode(Dropdown, {
                  modelValue: vm.data.targetLanguageId,
                  "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((vm.data.targetLanguageId) = $event)),
                  items: targetLanguages.value || [],
                  "value-field": "languageId",
                  "text-field": "displayName",
                  class: "language-selector-dropdown",
                  default: `<${_unref($t)('none')}>`,
                  onInput: selectTargetLanguage
                }, {
                  "item-template": _withCtx(({ entry }) => [
                    entry
                      ? (_openBlock(), _createElementBlock("span", _hoisted_8, _toDisplayString(entry.displayName), 1))
                      : _createCommentVNode("", true)
                  ]),
                  _: 1
                }, 8, ["modelValue", "items", "default"])
              ]),
              _: 1
            }, 8, ["label"])
          ]),
          _createVNode(_component_b_form_checkbox, {
            modelValue: vm.data.showOnlyOutdated,
            "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((vm.data.showOnlyOutdated) = $event))
          }, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(_unref($t)("translationManager.createNewDialog.outdatedTranslations")), 1)
            ]),
            _: 1
          }, 8, ["modelValue"])
        ])
      ]),
      _: 1
    }, 8, ["title", "modelValue"]),
    _createVNode(ManageCatalogs, {
      onSelectedCatalog: selectCatalog,
      "modal-name": "open-catalog",
      includedCatalogTypes: [0, 1],
      dialogMode: "open"
    })
  ], 64))
}
}

})